import type { ReactNode } from 'react';
import type { DragEndEvent, DragOverEvent, DragStartEvent, UniqueIdentifier } from '@dnd-kit/core';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

import { noop } from 'ui-components/default-props/noop';

import { UserSettings } from '../../../enums/user-settings/user-settings';
import { useHasOrderDetailsCustomLayoutFeature } from '../../../hooks/use-has-order-details-custom-layout-feature';
import { useUserSetting } from '../../../hooks/use-user-setting';
import { saveUserSetting } from '../../../store/user/user.actions';
import { useReturnIdContext } from '../context/return-id-context-provider';
import type {
  ColumnLayout,
  ColumnLayoutSetting,
  ColumnName,
  CustomizeLayoutContextValue,
  LayoutMode,
  SectionId,
} from './customize-layout-provider.d';

export const SECTION_IDS: Record<string, SectionId> = {
  fraudIndicators: 'FRAUD_INDICATORS',
  shipmentDetails: 'SHIPMENT_DETAILS',
  items: 'ITEMS',
  customs: 'CUSTOMS',
  notes: 'NOTES',
  customerComms: 'CUSTOMER_COMMUNICATION',
  activity: 'ACTIVITY',
  rateCardReadOnly: 'RATE_CARD_READ_ONLY',
  csw: 'CSW',
  oso: 'OSO',
} as const;

export const EMPTY_COLUMN_PLACEHOLDER_PREFIX = 'EMPTY_COLUMN_';

const DEFAULT_VISIBLE_SECTIONS: SectionId[] = [
  SECTION_IDS.fraudIndicators,
  SECTION_IDS.shipmentDetails,
  SECTION_IDS.items,
  SECTION_IDS.customs,
  SECTION_IDS.notes,
  SECTION_IDS.customerComms,
  SECTION_IDS.activity,
  SECTION_IDS.rateCardReadOnly,
  SECTION_IDS.csw,
  SECTION_IDS.oso,
];

const DEFAULT_COLUMN_LAYOUT: ColumnLayout = {
  column1: [
    SECTION_IDS.fraudIndicators,
    SECTION_IDS.shipmentDetails,
    SECTION_IDS.items,
    SECTION_IDS.customs,
    SECTION_IDS.notes,
    SECTION_IDS.customerComms,
    SECTION_IDS.activity,
  ],
  column2: [SECTION_IDS.rateCardReadOnly, SECTION_IDS.csw, SECTION_IDS.oso],
  column3: [],
};

const parseColumnLayout = (settingValue: string | undefined): ColumnLayoutSetting => {
  if (!settingValue) {
    return {
      ...DEFAULT_COLUMN_LAYOUT,
      layoutMode: 2,
    };
  }

  try {
    const parsed = JSON.parse(settingValue) as ColumnLayoutSetting;
    if (parsed && Array.isArray(parsed.column1) && Array.isArray(parsed.column2) && Array.isArray(parsed.column3)) {
      return parsed;
    }
  } catch {
    // Invalid JSON, fall back to default
  }

  return {
    ...DEFAULT_COLUMN_LAYOUT,
    layoutMode: 2,
  };
};

const findColumn = (itemId: UniqueIdentifier, columns: ColumnLayout): ColumnName | null => {
  if (columns.column1.includes(itemId as SectionId)) {
    return 'column1';
  }
  if (columns.column2.includes(itemId as SectionId)) {
    return 'column2';
  }
  if (columns.column3.includes(itemId as SectionId)) {
    return 'column3';
  }
  return null;
};

const CustomizeLayoutContext = createContext<CustomizeLayoutContextValue>({
  activeId: null,
  availableSectionIds: [],
  column1Order: [],
  column2Order: [],
  column3Order: [],
  isCustomizeMode: false,
  layoutMode: 2,
  onColumnLayoutChanged: noop,
  onCustomizeToggled: noop,
  onSectionVisibleToggled: noop,
  resetToDefaults: noop,
  shouldShowCustomizeToggle: false,
  shouldUseCustomLayout: false,
  visibleSections: [],
});

export const useCustomizeLayoutContext = () => useContext(CustomizeLayoutContext);

type CustomizeLayoutProviderProps = {
  children: ReactNode;
};

export const CustomizeLayoutProvider = ({ children }: CustomizeLayoutProviderProps) => {
  const dispatch = useDispatch();
  const savedLayoutSetting = useUserSetting(UserSettings.orderDetailsCustomLayout.settingName);
  const hasLabsFeature = useHasOrderDetailsCustomLayoutFeature();
  const returnId = useReturnIdContext();
  const isViewingReturn = !!returnId;

  const availableSectionIds = DEFAULT_VISIBLE_SECTIONS;

  const [isCustomizeMode, setIsCustomizeMode] = useState(false);

  const initialLayout = useMemo(() => parseColumnLayout(savedLayoutSetting), [savedLayoutSetting]);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  const [layoutMode, setLayoutMode] = useState<LayoutMode>(initialLayout.layoutMode || 2);
  const [columns, setColumns] = useState<ColumnLayout>({
    column1: initialLayout.column1,
    column2: initialLayout.column2,
    column3: initialLayout.column3,
  });

  const visibleSections = useMemo<SectionId[]>(() => {
    return [...columns.column1, ...columns.column2, ...columns.column3];
  }, [columns]);

  const resetToDefaults = useCallback(() => {
    setColumns({
      column1: DEFAULT_COLUMN_LAYOUT.column1,
      column2: DEFAULT_COLUMN_LAYOUT.column2,
      column3: DEFAULT_COLUMN_LAYOUT.column3,
    });
    setLayoutMode(2);
  }, []);

  const onCustomizeToggled = useCallback(
    (isActive: boolean) => {
      setIsCustomizeMode(isActive);
      if (isActive) {
        return;
      }
      const layoutToSave: ColumnLayoutSetting = {
        ...columns,
        layoutMode,
      };
      dispatch(
        saveUserSetting({
          settingName: UserSettings.orderDetailsCustomLayout.settingName,
          settingValue: JSON.stringify(layoutToSave),
          excludeV3prefix: true,
        }),
      );
    },
    [dispatch, columns, layoutMode],
  );

  const onSectionVisibleToggled = useCallback(
    (sectionId: SectionId, isVisible: boolean) => {
      if (isVisible) {
        setColumns((currentColumns) => {
          if (layoutMode === 1) {
            return {
              ...currentColumns,
              column1: [...currentColumns.column1, sectionId],
            };
          }
          if (layoutMode === 3) {
            return {
              ...currentColumns,
              column3: [...currentColumns.column3, sectionId],
            };
          }
          return {
            ...currentColumns,
            column2: [...currentColumns.column2, sectionId],
          };
        });
        return;
      }

      setColumns((currentColumns) => {
        return {
          ...currentColumns,
          column1: currentColumns.column1.filter((id) => id !== sectionId),
          column2: currentColumns.column2.filter((id) => id !== sectionId),
          column3: currentColumns.column3.filter((id) => id !== sectionId),
        };
      });
    },
    [layoutMode],
  );

  const onColumnLayoutChanged = useCallback((newLayoutMode: LayoutMode) => {
    setLayoutMode(newLayoutMode);
    setColumns((currentColumns) => {
      if (newLayoutMode === 1) {
        // Consolidate all sections into column1
        return {
          column1: [...currentColumns.column1, ...currentColumns.column2, ...currentColumns.column3],
          column2: [],
          column3: [],
        };
      }
      if (newLayoutMode === 2) {
        // Consolidate column3 into column2
        return {
          ...currentColumns,
          column2: [...currentColumns.column2, ...currentColumns.column3],
          column3: [],
        };
      }
      return currentColumns;
    });
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const getColumnFromPlaceholder = (itemId: UniqueIdentifier): ColumnName | null => {
        if (typeof itemId === 'string' && itemId.startsWith(EMPTY_COLUMN_PLACEHOLDER_PREFIX)) {
          return itemId.replace(EMPTY_COLUMN_PLACEHOLDER_PREFIX, '').toLowerCase() as ColumnName;
        }
        return null;
      };

      const sourceColumn = findColumn(active.id, columns);
      let targetColumn = findColumn(over.id, columns);

      if (!targetColumn) {
        targetColumn = getColumnFromPlaceholder(over.id);
      }

      if (!sourceColumn || !targetColumn) {
        return;
      }

      if (sourceColumn !== targetColumn) {
        setColumns((currentColumns) => {
          const targetOrder = currentColumns[targetColumn];
          let newTargetOrdering: SectionId[];

          if (!targetOrder.length || !targetOrder.includes(over.id as SectionId)) {
            newTargetOrdering = [...targetOrder, active.id as SectionId];
          } else {
            const targetOrderingIndex = targetOrder.indexOf(over.id as SectionId);
            const copy = [...targetOrder];
            copy.splice(targetOrderingIndex, 0, active.id as SectionId);
            newTargetOrdering = copy;
          }

          return {
            ...currentColumns,
            [sourceColumn]: currentColumns[sourceColumn].filter((id) => id !== active.id),
            [targetColumn]: newTargetOrdering,
          };
        });
      }
    },
    [columns],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      setActiveId(null);

      if (!over || active.id === over.id) {
        return;
      }

      const sourceColumn = findColumn(active.id, columns);
      const targetColumn = findColumn(over.id, columns);

      if (!sourceColumn || !targetColumn) {
        return;
      }

      if (sourceColumn === targetColumn) {
        setColumns((currentColumns) => {
          const currentOrdering = currentColumns[sourceColumn];

          const oldIndex = currentOrdering.indexOf(active.id as SectionId);
          const newIndex = currentOrdering.indexOf(over.id as SectionId);

          if (oldIndex !== -1 && newIndex !== -1) {
            return {
              ...currentColumns,
              [sourceColumn]: arrayMove(currentOrdering, oldIndex, newIndex),
            };
          }
          return currentColumns;
        });
      }
    },
    [columns],
  );

  const contextValue = useMemo(
    (): CustomizeLayoutContextValue => ({
      activeId,
      availableSectionIds,
      column1Order: columns.column1,
      column2Order: columns.column2,
      column3Order: columns.column3,
      isCustomizeMode,
      layoutMode,
      onColumnLayoutChanged,
      onCustomizeToggled,
      onSectionVisibleToggled,
      resetToDefaults,
      shouldShowCustomizeToggle: hasLabsFeature && !isViewingReturn,
      shouldUseCustomLayout: hasLabsFeature && (!!savedLayoutSetting || isCustomizeMode),
      visibleSections,
    }),
    [
      isCustomizeMode,
      isViewingReturn,
      layoutMode,
      visibleSections,
      columns,
      activeId,
      onColumnLayoutChanged,
      onCustomizeToggled,
      onSectionVisibleToggled,
      resetToDefaults,
      availableSectionIds,
      hasLabsFeature,
      savedLayoutSetting,
    ],
  );

  return (
    <CustomizeLayoutContext.Provider value={contextValue}>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} onDragOver={handleDragOver} onDragStart={handleDragStart}>
        {children}
      </DndContext>
    </CustomizeLayoutContext.Provider>
  );
};
